/**
 * `/bot` — public page about BystepBot, the reader behind "Bedah website" (website reverse
 * engineering, docs/reverse-engineering/PLAN.md): user agents, behaviour, opt-out, retention.
 * Landing-styled (`.mk`, light-only); code snippets reuse the tutorial's copyable block.
 */
import { createFileRoute } from '@tanstack/react-router'
import { Container, MkFooter, MkNav, Overline } from '#/components/landing/mk'
import { TutBlocks, TutCode } from '#/components/tutorial/tutorial-ui'
import { m } from '#/paraglide/messages'
import { BOT_UA, BROWSER_UA } from '#/lib/site/types'

// the address the legal pages use (privacy, terms) — opt-out requests are a legal matter
const CONTACT = 'hello@bystep.dev'
/** Language-neutral: the exact strings a site owner will see in logs and put in robots.txt. */
const UA_STATIC = [BOT_UA] as const
const UA_BROWSER = [BROWSER_UA] as const
const ROBOTS_BLOCK = ['User-agent: BystepBot', 'Disallow: /'] as const
const ROBOTS_SIGNAL = ['User-agent: *', 'Content-Signal: search=yes, ai-input=no, ai-train=no', 'Allow: /'] as const

export const Route = createFileRoute('/bot')({
  head: () => ({
    meta: [
      { title: m.bot_meta_title() },
      { name: 'description', content: m.bot_meta_desc() },
      { property: 'og:title', content: m.bot_meta_title() },
      { property: 'og:description', content: m.bot_meta_desc() },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://bystep.dev/brand/bystep-og.png' },
    ],
    links: [{ rel: 'canonical', href: 'https://bystep.dev/bot' }],
  }),
  component: BotPage,
})

function BotPage() {
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main id="main-content" data-testid="bot-page">
        <section className="pt-12 pb-6 lg:pt-16">
          <Container className="flex flex-col gap-5">
            <Overline>{m.bot_overline()}</Overline>
            <h1 className="max-w-[24ch]">{m.bot_title()}</h1>
            <p className="mk-lead max-w-[62ch]">{m.bot_lead()}</p>
            <p className="text-sm text-[var(--mk-fg-muted)]">{m.bot_updated()}</p>
          </Container>
        </section>

        <section className="py-6" aria-labelledby="bot-ua">
          <Container className="flex max-w-3xl flex-col gap-4">
            <h2 id="bot-ua">{m.bot_ua_title()}</h2>
            <p className="mk-tut-p">{m.bot_ua_lead()}</p>
            <TutCode caption={m.bot_ua_static_caption()} lines={UA_STATIC} />
            <TutCode caption={m.bot_ua_browser_caption()} lines={UA_BROWSER} />
          </Container>
        </section>

        <section className="py-6" aria-labelledby="bot-does">
          <Container className="flex max-w-3xl flex-col gap-4">
            <h2 id="bot-does">{m.bot_does_title()}</h2>
            <TutBlocks blocks={[{ kind: 'list', items: [m.bot_does_1(), m.bot_does_2(), m.bot_does_3(), m.bot_does_4(), m.bot_does_5(), m.bot_does_6()] }]} />
          </Container>
        </section>

        <section className="py-6" aria-labelledby="bot-never">
          <Container className="flex max-w-3xl flex-col gap-4">
            <h2 id="bot-never">{m.bot_never_title()}</h2>
            <TutBlocks blocks={[{ kind: 'list', items: [m.bot_never_1(), m.bot_never_2(), m.bot_never_3(), m.bot_never_4(), m.bot_never_5(), m.bot_never_6()] }]} />
          </Container>
        </section>

        <section id="opt-out" className="scroll-mt-24 py-6" aria-labelledby="bot-optout">
          <Container className="flex max-w-3xl flex-col gap-4">
            <h2 id="bot-optout">{m.bot_optout_title()}</h2>
            <p className="mk-tut-p">{m.bot_optout_lead()}</p>
            <TutCode caption={m.bot_optout_robots_caption()} lines={ROBOTS_BLOCK} />
            <TutCode caption={m.bot_optout_signal_caption()} lines={ROBOTS_SIGNAL} />
            <p className="mk-tut-p">{m.bot_optout_signal_note()}</p>
            <p className="mk-tut-p">
              {m.bot_optout_email({ email: '' })}
              <a className="underline underline-offset-2" href={`mailto:${CONTACT}`}>{CONTACT}</a>
            </p>
          </Container>
        </section>

        <section className="py-6 pb-20 lg:pb-24" aria-labelledby="bot-store">
          <Container className="flex max-w-3xl flex-col gap-4">
            <h2 id="bot-store">{m.bot_store_title()}</h2>
            <TutBlocks blocks={[{ kind: 'list', items: [m.bot_store_1(), m.bot_store_2(), m.bot_store_3(), m.bot_store_4()] }]} />
            <p className="text-sm text-[var(--mk-fg-muted)]">
              {m.bot_contact({ email: '' })}
              <a className="underline underline-offset-2" href={`mailto:${CONTACT}`}>{CONTACT}</a>
            </p>
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}
